/**
 * Seguranca Module Routes
 *
 * FASE 1 (2026-04-21): todas as rotas ganharam gate de permissão.
 * FASE 2 (2026-04-21): as 3 rotas duplicadas com /admin/* foram
 * substituídas por redirects — o stack moderno em /admin/perfis e
 * /admin/permissoes é o canônico. As páginas legadas em
 * `src/pages/seguranca/{GerenciamentoPerfis,CadastroPapéis,EdicaoPermissoes}.tsx`
 * continuam no repo para consulta histórica mas não são mais renderizadas.
 */

import { lazy } from "react";
import { Navigate } from "react-router-dom";
import { ModuleRoutes } from "../types";

// Lazy load components
const MFASSO = lazy(() => import("@/pages/seguranca/MFASSO"));
const AuditoriaAcesso = lazy(() => import("@/pages/seguranca/AuditoriaAcesso"));
const ConfiguracaoMFA = lazy(() => import("@/pages/seguranca/ConfiguracaoMFA"));
const HierarquiaPerfis = lazy(() => import("@/pages/seguranca/HierarquiaPerfis"));
const ParametrosGerais = lazy(() => import("@/pages/seguranca/ParametrosGerais"));
const ConfiguracoesTecnicasAvancadas = lazy(
  () => import("@/pages/seguranca/ConfiguracoesTecnicasAvancadas")
);
const DashboardAuditoria = lazy(() => import("@/pages/seguranca/DashboardAuditoria"));
const LogsAtividades = lazy(() => import("@/pages/seguranca/LogsAtividades"));
const LogsSeguranca = lazy(() => import("@/pages/seguranca/LogsSeguranca"));
const ExportacaoLogs = lazy(() => import("@/pages/seguranca/ExportacaoLogs"));
const AuditoriaSubstabelecidos = lazy(() => import("@/pages/seguranca/AuditoriaSubstabelecidos"));

export const segurancaRoutes: ModuleRoutes = {
  moduleId: "seguranca",
  basePath: "/seguranca",
  routes: [
    // ——————————————————————————————————————————————————————————————————————
    // Configurações globais de segurança (admin-only)
    // ——————————————————————————————————————————————————————————————————————
    {
      path: "/seguranca/mfa-sso",
      element: <MFASSO />,
      permission: { resource: "admin", action: "access" },
    },
    {
      path: "/seguranca/configuracao-mfa",
      element: <ConfiguracaoMFA />,
      permission: { resource: "admin", action: "access" },
    },
    {
      path: "/seguranca/parametros-gerais",
      element: <ParametrosGerais />,
      permission: { resource: "admin", action: "access" },
    },
    {
      path: "/seguranca/configuracoes-tecnicas-avancadas",
      element: <ConfiguracoesTecnicasAvancadas />,
      permission: { resource: "admin", action: "access" },
    },

    // ——————————————————————————————————————————————————————————————————————
    // RBAC — redirect para o stack moderno em /admin/* (FASE 2)
    // ——————————————————————————————————————————————————————————————————————
    {
      path: "/seguranca/perfis",
      element: <Navigate to="/admin/perfis" replace />,
    },
    {
      path: "/seguranca/cadastro-papéis",
      element: <Navigate to="/admin/perfis" replace />,
    },
    {
      path: "/seguranca/edicao-permissoes",
      element: <Navigate to="/admin/perfis" replace />,
    },

    // ——————————————————————————————————————————————————————————————————————
    // Hierarquia (mantém, não há equivalente em /admin)
    //
    // 🔴 `/seguranca/delegacao-permissoes` foi RETIRADA em 11/09/2026. A tela
    // afirmava "Delegação criada com sucesso!" e gravava a linha só no estado
    // do React — nenhuma permissão mudava, e atualizar a página apagava. Numa
    // área de SEGURANÇA, uma tela que afirma ter concedido acesso e não
    // concedeu é pior que tela nenhuma.
    //
    // Não é defeito de tela, é consequência de arquitetura: a API que ela
    // chamaria (`endpoints/permissions.py`, 8 endpoints) nunca foi incluída em
    // `api.py`, e o portão de permissão (`RBACService.check_permission`) não lê
    // `delegated_permissions` nem `user_granular_permissions`. Religar é
    // decisão de arquitetura — ver docs/RBAC-PERMISSOES-GRANULARES-GUIA-PORTAVEL.md
    // §11.1, "Dois sistemas de permissão, e o gate consulta só um".
    //
    // O componente continua no repositório, de propósito: quando essa decisão
    // for tomada, a tela já está desenhada.
    // ——————————————————————————————————————————————————————————————————————
    {
      path: "/seguranca/hierarquia-perfis",
      element: <HierarquiaPerfis />,
      permission: { resource: "roles", action: "read" },
    },

    // ——————————————————————————————————————————————————————————————————————
    // Auditoria
    // ——————————————————————————————————————————————————————————————————————
    {
      path: "/seguranca/auditoria-acesso",
      element: <AuditoriaAcesso />,
      permission: { resource: "audit", action: "read" },
    },
    {
      path: "/seguranca/dashboard-auditoria",
      element: <DashboardAuditoria />,
      permission: { resource: "audit", action: "read" },
    },
    {
      path: "/seguranca/auditoria-substabelecidos",
      element: <AuditoriaSubstabelecidos />,
      permission: { resource: "audit", action: "read" },
    },

    // ——————————————————————————————————————————————————————————————————————
    // Logs do sistema
    // ——————————————————————————————————————————————————————————————————————
    {
      path: "/seguranca/logs-atividades",
      element: <LogsAtividades />,
      permission: { resource: "system_logs", action: "read" },
    },
    {
      path: "/seguranca/logs-seguranca",
      element: <LogsSeguranca />,
      permission: { resource: "system_logs", action: "read" },
    },
    {
      path: "/seguranca/exportacao-logs",
      element: <ExportacaoLogs />,
      permission: { resource: "system_logs", action: "export" },
    },
  ],
};
